import { useMemo, useRef, useState } from 'react';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import { createTheme } from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import useTheme from '@material-ui/core/styles/useTheme';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import ThemeProvider from '@material-ui/styles/ThemeProvider';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import GetAppIcon from '@material-ui/icons/GetApp';
import CloseIcon from '@material-ui/icons/Close';
import { Upload as UploadIcon } from 'react-feather';
import clsx from 'clsx';
import { get as _get } from 'lodash';

import { downloadBlob, downloadServerFile, getTitleFromPath } from '../functions';
import { useForm, useFormRules, useAlert, useGTranslation } from '../hooks';
import { FileRule, FormFileDownload } from '../types';
import { linkBlue } from '../constants';

import FormInput from './FormInput';
import { FormikValues, setIn } from 'formik';

export interface FormFileProps {
	id: string;
	description: string;
	download?: FormFileDownload;
	helperText?: string;
	accepts?: string[];
	size?: number;
	noAxiosOnFileDownload?: boolean;
}

const FormFile: React.FC<FormFileProps> = ({ id, description, download, helperText: helperT, accepts: acceptsProps, size, noAxiosOnFileDownload }) => {
	const [fileKey, setFileKey] = useState(`${Math.random()}`);
	const [fileDownloadLoading, setFileDownloadLoading] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const classes = useStyles();

	const form = useForm();
	const formRules = useFormRules();
	const alert = useAlert();
	const translate = useGTranslation();
	const uf = translate("error.form.uploadFile");
	const ft = translate("error.form.fileType");
	const errors = translate("error.downloadFile");

	const originalTheme = useTheme();

	const filePath = useMemo(() => `${id}Path`, [id]);
	const fileName = useMemo(() => getTitleFromPath(_get(form.values, filePath), id), [form.values, filePath, id]);//todo check if need to change to form.values _getu
	const accept = useMemo(() => (acceptsProps ?? (formRules[id] as FileRule)?.accept ?? []), [formRules, id]);//todo what to do with yup?
	const hasDownload = useMemo(() => !!(download?.fieldNames && download?.label && download?.path), [download]);

	const helperText = useMemo(() => {
		if (!accept.length) return undefined;
		if (helperT) return helperT;

		const fileTypes = accept.map(accept => accept.split('/')[1]);

		return `${ft} ${fileTypes.join(', ')}`;
	}, [accept, ft]);

	const theme = useMemo(() => {
		if (fileName) return originalTheme;

		return createTheme({
			...originalTheme,
			palette: {
				primary: {
					main: 'rgba(0, 0, 0, 0)'
				}
			}
		})
	}, [fileName, originalTheme]);

	const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files ?? [];
		const file = files[0];
		if (!file) return form.setFieldTouched(filePath, true);

		if (!accept.includes(file.type)) return (
			!_get(form.touched, filePath) && await form.setFieldTouched(filePath, true),
			form.setFieldError(filePath, { path: 'error.image', values: [] } as any)
		);

		if (encodeURIComponent(decodeURIComponent(`${id}${file.name}`)).length > 260) return (
			!_get(form.touched, filePath) && await form.setFieldTouched(filePath, true),
			form.setFieldError(filePath, { path: 'error.fileNameLength', values: [] } as any)
		);

		if (size && file.size > size) return (
			!_get(form.touched, filePath) && await form.setFieldTouched(filePath, true),
			form.setFieldError(filePath, { path: 'error.tooHeavy', values: [] } as any)
		);

		await form.setValues((values: FormikValues) => {
			let temp = setIn(values, id, file);
			return setIn(temp, filePath, file.name);
		});
		form.setFieldTouched(filePath, true);
	}

	const handleInputOpen = () => {
		fileInputRef.current?.click();
	}

	const handleDelete = () => {
		form.setFieldTouched(filePath, true);
		form.setValues((values: FormikValues) => {
			let temp = setIn(values, id, null);
			return setIn(temp, filePath, null);
		});
		setFileKey(`${Math.random()}`);
	}

	const handleInputClick = async () => {
		try {
			if (!fileName) return;
			const file = _get(form.values, id) as Blob | null;
			if (file) return downloadBlob(file, fileName);
			return downloadServerFile(_get(form.values, filePath), fileName, undefined, undefined, noAxiosOnFileDownload);
		} catch (error) {
			alert(errors);
			console.error(error);
		}
	}

	const handleDownload = async () => {
		try {
			setFileDownloadLoading(true);
			await downloadServerFile(download!.path, getTitleFromPath(download!.path, download!.fieldNames), undefined, undefined, noAxiosOnFileDownload);
		} catch (error) {
			alert(errors);
			console.error(error);
		} finally {
			setFileDownloadLoading(false);
		}
	}

	return (
		<>
			<FormInput
				id={filePath}
				description={description}
				readOnly
				value={fileName}
				helperText={helperText}
				classes={{
					root: clsx(!fileName && classes.noFileRoot),
					input: clsx(fileName ? classes.fileInput : classes.noFileInput),
				}}
				inputProps={{
					onClick: handleInputClick
				}}
				labelAdornment={hasDownload && (
					<Button
						color="primary"
						onClick={handleDownload}
						disabled={fileDownloadLoading}
						startIcon={<GetAppIcon className={classes.downloadButtonIcon} />}
						classes={{
							root: classes.downloadButton,
							label: classes.downloadButtonLabel,
							startIcon: classes.downloadButtonStartIcon
						}}
					>
						{download!.label}
					</Button>
				)}
				startAdornment={(
					<ThemeProvider theme={theme}>
						<IconButton
							color="primary"
							disableRipple
							disableFocusRipple
							disableTouchRipple
							className={clsx(classes.fileIcon, !fileName && classes.noFileIcon, classes.iconButton)}
						>
							<InsertDriveFileIcon className={classes.svg} />
						</IconButton>
					</ThemeProvider>
				)}
				endAdornment={fileName ? (
					<IconButton
						color="primary"
						onClick={handleDelete}
						className={classes.iconButton}
					>
						<CloseIcon className={classes.svg} />
					</IconButton>
				) : (
					<Button
						color="primary"
						endIcon={(
							<UploadIcon
								className={classes.svg}
								color={originalTheme.palette.primary.main}
							/>
						)}
						classes={{
							root: classes.uploadButton,
							label: classes.noFileLabel,
							endIcon: classes.uploadButtonEndIcon
						}}
						onClick={handleInputOpen}
					>
						{uf}
					</Button>
				)}
			/>
			<input
				key={fileKey}
				ref={fileInputRef}
				type="file"
				className={classes.input}
				onChange={handleInputChange}
				accept={accept.join('|')}
			/>
		</>
	);
}

export default FormFile;

export const useStyles = makeStyles(theme => ({
	fileIcon: {
		background: 'transparent !important',
		cursor: 'inherit'
	},

	input: {
		display: 'none'
	},

	noFileInput: {
		width: 0,
		padding: '0 !important'
	},

	uploadButton: {
		width: '100%',
		padding: 0
	},

	fileInput: {
		color: linkBlue,
		textDecoration: 'underline',
		cursor: 'pointer'
	},

	noFileIcon: {
		width: 0,
		paddingLeft: '0 !important',
		paddingRight: '0 !important'
	},

	noFileRoot: {
		alignItems: 'stretch'
	},

	downloadButton: {
		[theme.breakpoints.up(500)]: {
			padding: '0 5px',
		} as CSSProperties,
		[theme.breakpoints.down(500)]: {
			padding: '0 5.7px',
		} as CSSProperties,
		[theme.breakpoints.up(1600)]: {
			padding: '0 6.5px',
		} as CSSProperties
	},

	downloadButtonLabel: {
		fontFamily: 'Assistant-Bold',
		textDecoration: 'underline',
		textTransform: 'none',

		[theme.breakpoints.up(500)]: {
			fontSize: 13,
		} as CSSProperties,
		[theme.breakpoints.down(500)]: {
			fontSize: 15,
		} as CSSProperties,
		[theme.breakpoints.up(1600)]: {
			fontSize: 17,
		} as CSSProperties
	},

	downloadButtonStartIcon: {
		margin: 0,
		[theme.breakpoints.up(500)]: {
			marginRight: 5,
		} as CSSProperties,
		[theme.breakpoints.down(500)]: {
			marginRight: 5.7,
		} as CSSProperties,
		[theme.breakpoints.up(1600)]: {
			marginRight: 6.5,
		} as CSSProperties
	},

	downloadButtonIcon: {
		[theme.breakpoints.up(500)]: {
			width: 16,
			height: 16,
		} as CSSProperties,
		[theme.breakpoints.down(500)]: {
			width: 18,
			height: 18,
		} as CSSProperties,
		[theme.breakpoints.up(1600)]: {
			width: 21,
			height: 21,
		} as CSSProperties
	},

	noFileLabel: {
		fontFamily: 'Assistant-Regular',
		textTransform: 'none',

		[theme.breakpoints.up(500)]: {
			fontSize: 17,
		} as CSSProperties,
		[theme.breakpoints.down(500)]: {
			fontSize: 22,
		} as CSSProperties,
		[theme.breakpoints.up(1600)]: {
			fontSize: 23,
		} as CSSProperties,
	},

	iconButton: {
		[theme.breakpoints.up(500)]: {
			padding: 8.88,
		} as CSSProperties,
		[theme.breakpoints.down(500)]: {
			padding: 11.4,
		} as CSSProperties,
		[theme.breakpoints.up(1600)]: {
			padding: 12,
		} as CSSProperties,
	},

	svg: {
		[theme.breakpoints.up(500)]: {
			width: 17,
			height: 17
		} as CSSProperties,
		[theme.breakpoints.down(500)]: {
			width: 22,
			height: 22
		} as CSSProperties,
		[theme.breakpoints.up(1600)]: {
			width: 23,
			height: 23
		} as CSSProperties,
	},

	uploadButtonEndIcon: {
		[theme.breakpoints.up(500)]: {
			marginLeft: 4.4,
			marginRight: -2.2
		} as CSSProperties,
		[theme.breakpoints.down(500)]: {
			marginLeft: 5.7,
			marginRight: -2.8
		} as CSSProperties,
		[theme.breakpoints.up(1600)]: {
			marginLeft: 6,
			marginRight: -3
		} as CSSProperties,
	}
}));